<template>
  <svg :id="id" ref="svgElement" class="bar-chart simcision-chart">
    <defs>
      <bar-chart-patterns :id="id" :bar-width="barWidth"></bar-chart-patterns>
    </defs>
  </svg>
</template>

<script setup lang="ts">
import { reactive, ref, computed, onMounted, watch } from "vue";
import { scaleBand, scaleLinear } from "d3-scale";
import { transition } from "d3-transition";
import { axisBottom } from "d3-axis";
import { select } from "d3-selection";
import { v4 } from "uuid";
import { useI18n } from "vue-i18n";

import {
  addRoundActionSymbols,
  drawColorScale,
  drawVerticalYAxis,
  scaleHeight,
  selectOrCreateChild
} from "./chartDrawFunction";
import { useChartEngine } from "./useChartEngine";
import BarChartPatterns from "./BarChartPatterns.vue";

import type { ScaleLinear } from "d3-scale";
import type { ChartComponentProps, ChartEmitEvents } from "./useChartEngine";
import type { DataEntry, EntryValue, ColumnEntry } from "./ChartData";

const { t } = useI18n();

const props = withDefaults(defineProps<ChartComponentProps>(), {
  id: () => v4(),
  yUnit: "%",
  width: 960,
  height: 500,
  settings: () => ({}),
  formattedTitle: () => [],
  animationsDisabled: false,
  titleClickable: false,
  margin: () => ({ top: 20, left: 20, bottom: 20, right: 20 })
});

const emit = defineEmits<ChartEmitEvents>();

const OUTER_PADDING = computed(() => {
  return (props.settings.fontSize || 12) * 2.5;
});

const ctx = useChartEngine(() => props, { emit });
// @ts-expect-error - svgElement is used as template ref
const { svgElement } = ctx; // Extracted for template ref binding (used in <svg ref="svgElement">)
const chart = reactive(ctx);

const yScaleByUnit = ref<{
  [unit: string]: ScaleLinear<number, number, never>;
}>({
  "%": scaleLinear()
});
function scaleY(unit: string, value: number | null): number {
  const scale = yScaleByUnit.value[unit];
  if (!scale) return 0;
  if (value == null) return scale(0);
  return scale(value);
}

const xScale = ref(scaleBand());

const barWidth = computed(() => {
  const minWidth = xScale.value.bandwidth();
  return Math.min(props.settings.areasColored ? 20 : 15, minWidth);
});

const resolvedColumns = computed(() => {
  return (
    props.columnData ||
    chart.longestValuesArray.reduce((entries, v) => {
      entries.set(v.columnId, {
        id: v.id,
        key: v.key,
        type: v.type,
        title: v.title,
        shortTitle: v.shortTitle,
        values: []
      });
      return entries;
    }, new Map<string, ColumnEntry>())
  );
});

const labelsRotated = computed(() => {
  const longestXLabel = [...resolvedColumns.value.values()].reduce(
    (longest, columnEntry) => {
      const title = getColumnTitle(columnEntry);
      return Math.max(title.length || 0, longest);
    },
    0
  );
  return longestXLabel >= 3;
});

function getColumnTitle(columnEntry?: ColumnEntry) {
  if (!columnEntry) return "";
  let title = props.settings.xAxisLabelsShortened
    ? columnEntry.shortTitle || ""
    : columnEntry.title || "";
  if (props.settings.xAxisLabelsShortened && columnEntry.groupShortTitle) {
    title = `${title} (${columnEntry.groupShortTitle})`;
  }
  if (!props.settings.xAxisLabelsShortened && columnEntry.groupTitle) {
    title = `${title} (${columnEntry.groupTitle})`;
  }
  return title;
}

const roundEntriesHeight = computed(() => {
  if (!props.settings.roundEffectsVisible) return 0;
  const columns = resolvedColumns.value;

  // Add height for round effects
  const columnRows =
    columns == null
      ? new Set<never>()
      : [...columns.values()].reduce((columnRowKeys, columnDataEntry) => {
          const values = columnDataEntry.values || [];
          for (const value of values) {
            if (!columnRowKeys.has(value.id)) columnRowKeys.add(value.id);
          }
          return columnRowKeys;
        }, new Set<string | number>());
  return columnRows.size * 16;
});

watch(
  [() => props.data, () => props.settings],
  () => {
    updateChart();
  },
  { deep: true }
);

watch([() => props.width, () => props.height], ([width, height]) => {
  if (!chart.svg) return;
  chart.svg.attr("width", width);
  chart.svg.attr("height", height);
  chart.svg
    .select(".chart__background")
    .attr("width", width)
    .attr("height", height);
  updateChart();
});

onMounted(() => {
  createChart();

  if (!props.animationsDisabled) {
    setTimeout(() => {
      chart.animationDuration = 500;
    }, 200);
  }
});

// =======
// METHODS

function createChart() {
  if (!chart.svgElement) return;

  const svg = select(chart.svgElement)
    .attr("width", props.width)
    .attr("height", props.height);
  chart.svg = svg;

  // Create the background rect
  // This is relevant for SVG exports.
  if (props.settings?.backgroundFilled !== false) {
    svg
      .append("rect")
      .attr("width", props.width)
      .attr("height", props.height)
      .attr("class", "chart__background");
  }

  chart.container = svg
    .append("g")
    .attr("class", "chart__container");

  const header = svg.append("g").attr("class", "chart__header");
  header.append("rect").attr("class", "chart__header-bg");
  header.append("text").attr("class", "chart__title");

  chart.container.append("g").attr("class", "gx");
  chart.container.append("g").attr("class", "gy");
  chart.container.append("g").attr("class", "chart-colorscale");
  chart.container.append("g").attr("class", "chart__groups");

  updateChart();
}

function updateChart() {
  if (!chart.svg) return;
  chart.svg.attr("width", props.width).attr("height", props.height);

  updateLabelsHeight();
  positionContainer();

  updateScales();

  chart.drawTitle();

  drawAxes();

  drawScaleColors();

  updateBars();

  drawXAxisLabel();
  drawYAxisLabels();
}

function positionContainer() {
  if (!chart.container) return;
  chart.paddingLeft = chart.yTicksWidth;

  if (props.settings.scaleColorsVisible) {
    chart.paddingLeft += 12;
  }

  if (props.settings.xAxisTitleVisible) {
    const xAxisLabelString = props.settings.xAxis
      ? t(`chartAxes.${props.settings.xAxis}`)
      : "";
    const axisTitleWidth = chart.measureTextWidthCached(xAxisLabelString, props.settings.fontSize || 12);
    chart.paddingRight = axisTitleWidth;
  } else {
    chart.paddingRight = 0;
  }

  chart.container
    .transition()
    .duration(chart.animationDuration / 5)
    .attr(
      "transform",
      `translate(${props.margin.left + chart.paddingLeft}, ${
        props.margin.top + chart.titleHeight
      })`
    );
}

function updateLabelsHeight() {
  if (!props.settings.xAxisLabelsVisible && !props.settings.xAxisTitleVisible) {
    chart.labelsHeight = 0;
    return;
  }

  if (!props.settings.xAxisLabelsVisible) {
    chart.labelsHeight = (props.settings.fontSize || 12) * 1.25;
    return;
  }

  let xAxisTickHeight = 25;

  const columns = resolvedColumns.value;

  if (labelsRotated.value) {
    let longestWidth = 0;

    const labelsFontSize = (props.settings.fontSize || 12) / 12 * 10;
    for (const [columnId] of columns) {
      const labelText = getColumnTitle(columns.get(columnId));
      const textLength = chart.measureTextWidthCached(labelText, labelsFontSize);
      longestWidth = Math.max(longestWidth, textLength);
    }

    const hypothenuse = Math.round(longestWidth);

    xAxisTickHeight = Math.round(Math.sqrt(Math.pow(hypothenuse, 2) / 2) + labelsFontSize / 2);
  }

  chart.labelsHeight = xAxisTickHeight + roundEntriesHeight.value;
}

function updateScales() {
  updateXScale();
  updateYScale();
}

function updateXScale() {
  const columnGroups: { uid: string, columnIds: Set<string> }[] = [];
  for (const group of props.data) {
    for (const entry of group.values) {
      let uidGroup = columnGroups.find(u => u.uid === entry.uid);
      if (!uidGroup) {
        uidGroup = { uid: entry.uid, columnIds: new Set<string>() };
        columnGroups.push(uidGroup);
      }
      uidGroup.columnIds.add(entry.columnId);
    }
  }

  const allEntries: string[] = [];
  for (const group of columnGroups) {
    allEntries.push(...Array.from(group.columnIds));
  }

  // x axis uses a band scale.
  xScale.value = scaleBand()
    .range([0, chart.innerWidth])
    .domain(allEntries);

  // adjust padding based on outer padding constant
  const step = xScale.value.step();
  const s = OUTER_PADDING.value / step;
  xScale.value.padding(0).paddingOuter(s);
}

function updateYScale() {
  for (const unit of chart.yUnits) {
    // y axis uses a linear scale.
    const scale = scaleLinear().range([chart.innerHeight, 0]);

    const unitDomain = chart.unitDomains[unit];

    yScaleByUnit.value[unit] = scale.domain([
      Math.min(unitDomain[0], 0),
      Math.max(unitDomain[1], 0)
    ]);
  }
}

function drawScaleColors() {
  if (!chart.container) return;

  const percentUnitIndex = chart.yUnits.indexOf("%");
  const colorScale = drawColorScale(chart.container, yScaleByUnit.value["%"], {
    height: chart.innerHeight,
    id: props.id,
    scaleColors: props.settings.scaleColors,
    scaleColorsVisible:
      props.settings.scaleColorsVisible && percentUnitIndex >= 0,
    animationDuration: chart.animationDuration
  });
  if (percentUnitIndex >= 0 && colorScale) {
    if (props.settings.yAxisLabelsVisible) {
      const i = percentUnitIndex;
      const unitsBefore = chart.yUnits.slice(i + 1);
      const barX = unitsBefore.reduce(
        (sum, u) => (chart.yUnitWidths?.[u] ?? 50) + sum,
        0
      );
      const gapOffset = Math.max(0, (chart.yUnits.length - 1 - i) * 25);

      colorScale
        .transition()
        .duration(chart.animationDuration)
        .attr("transform", `translate(${-barX - gapOffset}, 0)`);
    } else {
      colorScale
        .transition()
        .duration(chart.animationDuration)
        .attr("transform", `translate(0, 0)`);
    }
  }
}

function drawAxes() {
  const g = chart.container;
  if (!g) return;

  // =======
  // Y AXIS
  drawVerticalYAxis(chart.yUnits, g.select(".gy"), yScaleByUnit.value, {
    ...props.settings,
    width: chart.innerWidth,
    animationDuration: chart.animationDuration,
    yUnitWidths: chart.yUnitWidths
  });

  // ========
  // X AXIS

  const columnData = resolvedColumns.value;
  const columnKeys = columnData ? [...columnData.keys()] : [];

  const columnWidth = xScale.value.step();

  // if the column width is too small, only show every xth column
  const minColumnWidth = 25;
  const minColumnWidthIndex = Math.max(Math.floor(minColumnWidth / columnWidth), 1);
  const displayedColumnKeys = columnKeys.filter((_, i) => {
    if (i == 0) return true; // show the first column
    const lastIndex = columnKeys.length - 1;
    if (i == lastIndex) return true; // show the last column
    if (i > lastIndex - minColumnWidthIndex) return false; // show the last columns
    return (i + 1) % minColumnWidthIndex === 0;
  });
  const displayedColumnKeysSet = new Set(displayedColumnKeys);
  const condensed = columnKeys.length > displayedColumnKeys.length;

  const generateXAxis = axisBottom(xScale.value)
    .tickSize(0)
    .tickPadding(8)
    .tickFormat((columnId) => {
      if (!displayedColumnKeysSet.has(columnId)) {
        return "";
      }
      if (columnData?.has(columnId)) {
        return getColumnTitle(columnData.get(columnId));
      }
      return columnId;
    });

  // draw column labels:
  if (!props.settings.xAxisLabelsVisible && !props.settings.roundEffectsVisible) {
    g.select(".gx").selectChildren().remove();
  } else {
    let gX = g.select<SVGGElement>(".gx");
    if (gX.empty()) {
      gX = g.append("g").attr("class", "gx");
    }
    gX.classed("-condensed", condensed);

    // create X axis
    const xAxisTransition = gX
      .transition()
      .duration(chart.animationDuration)
      .call(generateXAxis)
      .attr(
        "transform",
        `translate(0, ${chart.innerHeight + roundEntriesHeight.value + 3})`
      );
    
    if (props.settings.xAxisLabelsVisible) {
      // Rotate X labels if the text is too long.
      if (labelsRotated.value) {
        gX.selectAll<SVGTextElement, string>(".tick > text")
          .attr("transform", "translate(-10,0)rotate(-45)")
          .style("text-anchor", "end");
      } else {
        // un-rotate the labels
        gX.selectAll<SVGTextElement, string>(".tick > text")
          .attr("transform", "translate(0,0)rotate(0)")
          .style("text-anchor", "middle");
      }

      const labelsFontSize = (props.settings.fontSize || 12) / 12 * 10;

      gX.selectAll<SVGTextElement, string>(".tick > text").attr("font-size", labelsFontSize);

      // Add click event to short column titles (open rename popup)
      if (props.settings.xAxisLabelsShortened) {
        gX.selectAll<SVGTextElement, string>(".tick > text").on(
          "dblclick",
          (event: PointerEvent, columnId) => {
            const currentEntry = columnData.get(columnId);
            if (currentEntry) {
              emit("click:column-title", {
                uid: currentEntry.key,
                title: currentEntry.title || "",
                shortTitle: currentEntry.shortTitle || "",
                groupTitle: currentEntry.groupTitle,
                groupShortTitle: currentEntry.groupShortTitle,
                type: currentEntry.type,
                id: currentEntry.id,
                withSimulation: !!currentEntry.groupTitle
              });
            }
          }
        );
      }
    }

    // mark ticks for which no data exists
    gX.selectAll(".tick").classed("-null", (columnId) => {
      return !doesColumnHaveData(columnId as string);
    });

    // add title tooltips
    xAxisTransition
      .end()
      .then(() => {
        if (!gX) return;

        gX.selectAll<SVGTextElement, string>(".tick > text")
          .selectAll("title")
          .data((columnId) => [columnId])
          .join("title")
          .text((columnId) => props.settings.xAxisLabelsVisible ? (columnData?.get(columnId)?.title || "") : "");
      })
      .catch((e) => {
        if (e) {
          console.warn(e);
        }
      });
  }

  if (props.settings.roundEffectsVisible) {
    addRoundActionSymbols(
      g,
      columnData,
      (event, position, entry) => {
        emit("open-round-actions", {
          entry,
          position
        });
      },
      {
        animationDuration: chart.animationDuration,
        columnWidth: xScale.value.step(),
        height: roundEntriesHeight.value
      }
    );
  } else {
    g.selectAll(".gx .chart-roundactions").remove();
    g.selectAll(".gx .roundactions-simsymbol").remove();
  }
}

function doesColumnHaveData(columnId: string): boolean {
  for (const group of props.data) {
    for (const entry of group.values) {
      if (entry.columnId === columnId && entry.value != null) {
        return true;
      }
    }
  }
  return false;
}

function drawYAxisLabels() {
  if (!props.settings.yAxisTitleVisible) {
    chart.container?.selectAll(".axis-label--y").remove();
    return;
  }
  const g = chart.container;
  if (!g) return;

  const data = chart.yUnits.map((unit) => ({
    unit,
    color: chart.unitColors[unit],
    key: props.settings.yAxis + unit
  }));

  const fontSize = props.settings.fontSize || 12;
  const gapWidth = fontSize * 2;

  g.selectAll(".axis-label--y")
    .data(data, (d) => (d as { key: string }).key)
    .join(
      (enter) => {
        const group = enter
          .append("g")
          .attr("class", "axis-label--y")
          .on("click", (event) => {
            emit("click:axis-title", {
              axis: "y",
              x: event.clientX,
              y: event.clientY
            });
          })
          .attr("transform", (d) => {
            const i = chart.yUnits.indexOf(d.unit);
            const unitsBefore = chart.yUnits.slice(i + 1);
            const barX = unitsBefore.reduce(
              (sum, u) => (chart.yUnitWidths?.[u] ?? gapWidth * 2) + sum,
              0
            );
            const gapOffset = Math.max(0, (chart.yUnits.length - 1 - i) * gapWidth);

            return `translate(${-barX - gapOffset}, 0)`;
          });

        // shadow
        group
          .append("text")
          .attr("class", "axis-label__shadow")
          .attr("x", -10)
          .attr("y", 2)
          .attr("dy", "1em")
          .attr("text-anchor", "end")
          .attr("transform", "rotate(-90)")
          .attr("font-size", fontSize)
          .attr("stroke", "var(--color-background-deep)")
          .attr("stroke-width", 4)
          .attr("stroke-linejoin", "round")
          .attr("stroke-linecap", "round")
          .style("opacity", 0.7)
          .text((d) => {
            const text = props.settings.yAxis
              ? t(`chartAxes.${props.settings.yAxis}`)
              : "";
            return text + (d.unit.trim() ? ` [ ${d.unit} ]` : "");
          });

        // text
        group
          .append("text")
          .attr("class", "axis-label__text")
          .attr("x", -10)
          .attr("y", 2)
          .attr("dy", "1em")
          .attr("text-anchor", "end")
          .attr("transform", "rotate(-90)")
          .attr("font-size", fontSize)
          .attr("fill", (d) => {
            if (props.settings.areasColored) {
              return d.color ?? "var(--color-text)";
            }
            return "var(--color-text)";
          })
          .text((d) => {
            const text = props.settings.yAxis
              ? t(`chartAxes.${props.settings.yAxis}`)
              : "";
            return text + (d.unit.trim() ? ` [ ${d.unit} ]` : "");
          });

        return group;
      },
      (update) => {
        update
          .transition()
          .duration(chart.animationDuration)
          .attr("transform", (d) => {
            const i = chart.yUnits.indexOf(d.unit);
            const unitsBefore = chart.yUnits.slice(i + 1);
            const barX = unitsBefore.reduce(
              (sum, u) => (chart.yUnitWidths?.[u] ?? gapWidth * 2) + sum,
              0
            );
            const gapOffset = Math.max(0, (chart.yUnits.length - 1 - i) * gapWidth);

            return `translate(${-barX - gapOffset}, 0)`;
          });

        update
          .select(".axis-label__text")
          .text((d) => {
            const text = props.settings.yAxis
              ? t(`chartAxes.${props.settings.yAxis}`)
              : "";
            return text + (d.unit.trim() ? ` [ ${d.unit} ]` : "");
          })
          .attr("fill", (d) => {
            if (props.settings.areasColored) {
              return d.color ?? "var(--color-text)";
            }
            return "var(--color-text)";
          })
          .attr("font-size", fontSize);

        update.select(".axis-label__shadow").text((d) => {
          const text = props.settings.yAxis
            ? t(`chartAxes.${props.settings.yAxis}`)
            : "";
          return text + (d.unit.trim() ? ` [ ${d.unit} ]` : "");
        });

        return update;
      },
      (exit) => exit.remove()
    );
}

function drawXAxisLabel() {
  if (!props.settings.xAxisTitleVisible) {
    chart.container?.select(".axis-label--x").remove();
    return;
  }
  const g = chart.container;
  if (!g) return;

  // draw x axis label
  const xAxisLabelString = props.settings.xAxis
    ? t(`chartAxes.${props.settings.xAxis}`)
    : "";

  if (g.select(".axis-label--x").empty()) {
    g.append("g")
      .attr("class", "axis-label--x")
      .attr(
        "transform",
        `translate(${chart.innerWidth - OUTER_PADDING.value + 15}, ${
          chart.innerHeight
        })`
      )
      .on("click", (event) => {
        emit("click:axis-title", {
          axis: "x",
          x: event.clientX,
          y: event.clientY
        });
      });
  }

  const xAxisLabel = g.select(".axis-label--x");

  xAxisLabel
    .transition()
    .duration(chart.animationDuration)
    .attr(
      "transform",
      `translate(${chart.innerWidth - OUTER_PADDING.value + 15}, ${
        chart.innerHeight
      })`
    );

  // shadow
  selectOrCreateChild(xAxisLabel, "text.axis-label__shadow")
    .attr("x", 0)
    .attr("y", 5)
    .attr("dy", "1em")
    .attr("text-anchor", "start")
    .attr("font-size", props.settings.fontSize || 12)
    .attr("stroke", "var(--color-background-deep)")
    .attr("stroke-width", 4)
    .attr("stroke-linejoin", "round")
    .attr("stroke-linecap", "round")
    .style("opacity", 0.7)
    .text(xAxisLabelString);

  // text
  selectOrCreateChild(xAxisLabel, "text.axis-label__text")
    .attr("x", 0)
    .attr("y", 5)
    .attr("dy", "1em")
    .attr("text-anchor", "start")
    .attr("font-size", props.settings.fontSize || 12)
    .attr("fill", "var(--color-text)")
    .text(xAxisLabelString);

  const width = chart.measureTextWidthCached(xAxisLabelString, props.settings.fontSize || 12);

  selectOrCreateChild(xAxisLabel, "path")
    .attr(
      "d",
      `M0,0.5 L${width + 5},0.5 L${width + 2},-2 L${width + 5},0.5 L${
        width + 2
      },3`
    )
    .attr("stroke", "var(--color-text-light)")
    .attr("fill-opacity", "0.0");
}

function updateBars() {
  const g = chart.container;
  if (!g) return;

  const data = chart.filteredData;
  const scale = xScale.value;

  const MAIN_BAR_COLOR = "hsl(207, 38%, 34%)";

  const groups = g
    .select(".chart__groups")
    .selectAll<SVGGElement, DataEntry>(".chart__group")
    .data(data, (d) => d.key)
    .join(
      (enter) => {
        const barGroups = enter
          .append("g")
          .attr("class", "chart__group");

        return barGroups;
      },
      (update) => update,
      (exit) => exit.remove()
    );

  const COLUMN_WIDTH = scale.bandwidth();
  const BAR_WIDTH = barWidth.value;
  const BAR_FILL_PADDING = 0.4;
  const BAR_OFFSET = COLUMN_WIDTH / 2 - BAR_WIDTH / 2;

  const bars = groups
    .selectAll<SVGGElement, EntryValue>(".bar-chart__bar")
    .data(
      (d) => d.values,
      (c) => (c as { key: string }).key
    )
    .join(
      (enter) => {
        const bar = enter
          .append("g")
          .attr("class", "bar-chart__bar")
          .attr(
            "transform",
            (d) => `translate(${(scale(d.columnId) ?? 0) + BAR_OFFSET})`
          );

        bar
          .append("rect")
          .attr("class", "bar-chart__bar--main")
          .attr("width", BAR_WIDTH)
          .attr("y", (d) =>
            scaleY(d.unit, Math.max(d.startValue || 0, 0))
          )
          .attr("height", (d) => {
            return scaleHeight(
              yScaleByUnit.value[d.unit],
              Math.abs(d.startValue || 0)
            );
          });

        bar
          .append("rect")
          .attr("class", "bar-chart__bar--descending")
          .style("fill", () =>
            props.settings.areasColored
              ? `url('#${props.id}-bar-chart__pattern-descending--transparent')`
              : `url('#${props.id}-bar-chart__pattern-descending')`
          )
          .attr("width", BAR_WIDTH + BAR_FILL_PADDING)
          .attr("height", 0)
          .attr(
            "transform",
            (d) =>
              `translate(0, ${scaleY(d.unit, d.startValue || 0)})`
          );

        bar
          .append("rect")
          .attr("class", "bar-chart__bar--ascending")
          .style("fill", () =>
            props.settings.areasColored
              ? `url('#${props.id}-bar-chart__pattern-ascending--transparent')`
              : `url('#${props.id}-bar-chart__pattern-ascending')`
          )
          .attr("width", BAR_WIDTH + BAR_FILL_PADDING)
          .attr("height", 0)
          .attr(
            "transform",
            (d) =>
              `translate(0, ${scaleY(d.unit, d.startValue || 0)})`
          );

        bar.append("g").attr("class", "bar-chart__positive");
        bar.append("g").attr("class", "bar-chart__negative");

        bar
          .append("rect")
          .attr("class", "bar-chart__startvalueline")
          .attr("width", BAR_WIDTH)
          .attr("height", 1)
          .attr("y", (d) => scaleY(d.unit, d.startValue || 0));

        return bar;
      },
      (update) => {
        update
          .select(".bar-chart__bar--ascending")
          .style("fill", () =>
            props.settings.areasColored
              ? `url('#${props.id}-bar-chart__pattern-ascending--transparent')`
              : `url('#${props.id}-bar-chart__pattern-ascending')`
          );
        update
          .select(".bar-chart__bar--descending")
          .style("fill", () =>
            props.settings.areasColored
              ? `url('#${props.id}-bar-chart__pattern-descending--transparent')`
              : `url('#${props.id}-bar-chart__pattern-descending')`
          );

        return update;
      },
      (exit) => exit.remove()
    );

  // position every bar group on the x axis.
  bars
    .transition()
    .duration((chart.animationDuration * 3) / 5)
    .style("--color", (d) => d.color || "#fff")
    .attr(
      "transform",
      (d) => `translate(${(scale(d.columnId) ?? 0) + BAR_OFFSET})`
    );
  
  bars.classed("-null", (d) => d.value == null);

  const TRANSITION_DURATION = chart.animationDuration;
  const TRANSITION_DELAY = 0;

  if (props.settings.subValuesVisible) {
    bars.select(".bar-chart__bar--descending").attr("opacity", 0);
    bars.select(".bar-chart__bar--ascending").attr("opacity", 0);
    bars.select(".bar-chart__bar--main").attr("opacity", 0);

    // remove any event listeners from other chart configs
    bars.on("mousemove", null).on("mouseout", null);

    bars
      .select(".bar-chart__positive")
      .selectAll(".bar-chart__segment")
      .data(
        (d) => (d.subValues || []).filter((v) => (v.value || 0) > 0),
        (c) => (c as { key: string }).key
      )
      .join(
        (enter) =>
          enter
            .append("rect")
            .attr("class", "bar-chart__segment -positive")
            .attr("y", (d) => scaleY(d.unit, 0))
            .on("mousemove", (event, d) => {
              const x = event.clientX;
              const y = event.clientY;

              chart.updateHoverBox(
                { x, y },
                {
                  ...d,
                  value: (d.value || 0) - (d.startValue || 0),
                  startValue: null,
                  numberFormat: "pointsFlexible"
                }
              );
            })
            .on("mouseout", () => {
              chart.closeHoverBox();
            }),
        (update) => update,
        (exit) => exit.remove()
      )
      .attr("width", BAR_WIDTH)
      .attr("class", (d) => {
        let className = "bar-chart__segment -positive ";
        className += `-type-${d.type}`;
        return className;
      })
      .transition()
      .duration(TRANSITION_DURATION)
      .attr("y", (d) => scaleY(d.unit, (d.yStart || 0) + (d.value || 0)))
      .attr(
        "height",
        (d) => scaleHeight(yScaleByUnit.value[d.unit], d.value || 0) + 0.1
      );

    bars
      .select(".bar-chart__negative")
      .selectAll(".bar-chart__segment")
      .data(
        (d) => (d.subValues || []).filter((v) => (v.value || 0) < 0),
        (c) => (c as { key: string }).key
      )
      .join(
        (enter) => {
          const segment = enter
            .append("rect")
            .attr("class", "bar-chart__segment -negative")
            .attr("y", (d) => scaleY(d.unit, 0))
            .on("mousemove", (event, d) => {
              const x = event.clientX;
              const y = event.clientY;

              chart.updateHoverBox(
                { x, y },
                {
                  ...d,
                  value: (d.value || 0) - (d.startValue || 0),
                  startValue: null,
                  numberFormat: "pointsFlexible"
                }
              );
            })
            .on("mouseout", () => {
              chart.closeHoverBox();
            });

          return segment;
        },
        (update) => update,
        (exit) => exit.remove()
      )
      .attr("class", (d) => {
        let className = "bar-chart__segment -negative ";
        className += `-type-${d.type}`;
        return className;
      })
      .attr("width", BAR_WIDTH)
      .transition()
      .duration(TRANSITION_DURATION)
      .attr("y", (d) => scaleY(d.unit, d.yStart || 0))
      .attr("height", (d) =>
        scaleHeight(yScaleByUnit.value[d.unit], Math.abs(d.value || 0))
      );
  }
  // NO SUB VALUES
  else {
    bars.selectAll(".bar-chart__segment").remove();

    bars
      .on("mousemove", (event, d) => {
        const x = event.clientX;
        const y = event.clientY;

        chart.updateHoverBox({ x, y }, d);
      })
      .on("mouseout", () => {
        chart.closeHoverBox();
      });

    bars
      .select(".bar-chart__bar--descending")
      .attr("width", BAR_WIDTH + BAR_FILL_PADDING)
      .transition(
        transition().duration(TRANSITION_DURATION).delay(TRANSITION_DELAY)
      )
      .attr("opacity", 1)
      .attr(
        "transform",
        (d) =>
          `translate(${-BAR_FILL_PADDING / 2}, ${scaleY(d.unit, d.startValue || 0)})`
      )
      .attr("height", (d) => {
        if (d.value == null) {
          return 0;
        }
        const difference = Math.max((d.startValue || 0) - d.value, 0);
        return scaleHeight(yScaleByUnit.value[d.unit], difference);
      });

    bars
      .select(".bar-chart__bar--ascending")
      .attr("width", BAR_WIDTH + BAR_FILL_PADDING)
      .transition(
        transition().duration(TRANSITION_DURATION).delay(TRANSITION_DELAY)
      )
      .attr("opacity", 1)
      .attr(
        "transform",
        (d) =>
          `translate(${-BAR_FILL_PADDING / 2}, ${scaleY(d.unit, d.value || 0)})`
      )
      .attr("height", (d) => {
        if (d.value == null) {
          return 0;
        }
        const difference = Math.max(d.value - (d.startValue || 0), 0);
        return scaleHeight(yScaleByUnit.value[d.unit], difference);
      });

    bars
      .select(".bar-chart__bar--main")
      .attr("width", BAR_WIDTH)
      .style("fill", (d) =>
        props.settings.areasColored ? d.color ?? MAIN_BAR_COLOR : MAIN_BAR_COLOR
      )
      .transition(
        transition().duration(TRANSITION_DURATION).delay(TRANSITION_DELAY)
      )
      .attr("opacity", 1)
      .attr("height", (d) => {
        if (d.value == null) {
          return 0;
        }
        const v = Math.max(Math.abs(d.startValue || 0), Math.abs(d.value || 0));
        return scaleHeight(yScaleByUnit.value[d.unit], v);
      })
      .attr("y", (d) => {
        if (d.value == null) {
          return scaleY(d.unit, 0);
        }
        const startValue = d.startValue || 0;
        if (startValue >= 0 && d.value >= 0) {
          const v = Math.max(startValue, d.value);
          return scaleY(d.unit, v);
        } else {
          return scaleY(d.unit, 0);
        }
      });
  }

  // START VALUE INDICATOR
  bars
    .select(".bar-chart__startvalueline")
    .attr("width", BAR_WIDTH)
    .transition(
      transition().duration(TRANSITION_DURATION).delay(TRANSITION_DELAY)
    )
    .style("fill", MAIN_BAR_COLOR)
    .attr("y", (d) => scaleY(d.unit, d.startValue || 0));

  // SYMBOLS
  const symbols = bars
    .selectAll<SVGElement, EntryValue>(".chart__symbol")
    .data(
      (d) => {
        if (!props.settings.endPointsVisible) {
          return [];
        }
        if (d.value == null) {
          return [];
        }
        return [d];
      },
      (d) => d.key
    )
    .join(
      (enter) => {
        const symbol = chart.appendSymbol(enter);
        symbol
          .style("--resting-scale", 0.3)
          .style("--shadow-opacity", 0.4)
          .style("--fill-opacity", 1)
          .style("--color", MAIN_BAR_COLOR)
          .attr(
            "transform",
            (d) =>
              `translate(${BAR_WIDTH / 2}, ${scaleY(d.unit, d.startValue || 0)})`
          );
        return symbol;
      },
      (update) => update,
      (exit) => exit.remove()
    );
  symbols
    .transition(
      transition().duration(TRANSITION_DURATION).delay(TRANSITION_DELAY)
    )
    .attr(
      "transform",
      (d) =>
        `translate(${BAR_WIDTH / 2}, ${scaleY(d.unit, d.value || 0)})`
    );
  chart.updateSymbol(symbols);
}
</script>

<style lang="scss">
@use "./chart.scss";

body {
  --effect-type-scheduledEffect: hsl(25, 38%, 43%);
  --effect-type-action: hsl(39, 75%, 75%);
  --effect-type-delayed_action: hsl(39, 85%, 80%);
  --effect-type-incident: hsl(21, 79%, 74%); // hsl(40, 75%, 76%);
  --effect-type-delayed_incident: hsl(21, 79%, 80%); // hsl(40, 100%, 90%);
  --effect-type-relation: hsl(220, 61%, 80%); // hsl(185, 55%, 80%);
  --effect-type-momentum: hsl(212, 62%, 40%); // hsl(220, 60%, 80%);
  --effect-type-flowBranch: hsl(190, 40%, 70%);
}
.bar-chart {
  &__bar {
    &--ascending {
      fill: rgb(194, 244, 217);
    }
    &--descending {
      fill: rgb(229, 177, 179);
    }
    &.-null &--main {
      opacity: 0;
    }

    &.-null {
      opacity: 0.6;
    }
  }

  &__segment {
    &.-type-scheduledEffect {
      fill: var(--effect-type-scheduledEffect);
    }
    &.-type-action {
      fill: var(--effect-type-action);
    }
    &.-type-delayed_action {
      fill: var(--effect-type-delayed_action);
    }
    &.-type-incident {
      fill: var(--effect-type-incident);
    }
    &.-type-delayed_incident {
      fill: var(--effect-type-delayed_incident);
    }
    &.-type-relation {
      fill: var(--effect-type-relation);
    }
    &.-type-momentum {
      fill: var(--effect-type-momentum);
    }
    &.-type-flowBranch {
      fill: var(--effect-type-flowBranch);
    }

    &:hover {
      filter: brightness(80%) saturate(170%);
    }
  }
}
</style>
